import { useState, useEffect, useCallback, useRef } from 'react';
import classNames from 'classnames';

/**
 * Custom header checkbox for AG Grid's selection column.
 * Needed because the infinite row model doesn't support the built-in header checkbox.
 * Selects/deselects all loaded (filtered) rows.
 */
export function SelectAllHeaderCheckbox({ api }) {
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  // Redirect focus from the AG Grid header cell to the actual input
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const headerCell = wrapper.closest('.ag-header-cell');
    if (!headerCell) return;

    const handleFocusIn = () => {
      inputRef.current?.focus();
    };
    headerCell.addEventListener('focusin', handleFocusIn);
    return () => headerCell.removeEventListener('focusin', handleFocusIn);
  }, []);

  const updateState = useCallback(() => {
    if (!api) return;

    let total = 0;
    let selected = 0;

    api.forEachNode(node => {
      if (!node.data) return;
      total++;
      if (node.isSelected()) selected++;
    });

    setChecked(total > 0 && selected === total);
    setIndeterminate(selected > 0 && selected < total);
  }, [api]);

  useEffect(() => {
    if (!api) return;

    const onSelectionChanged = () => updateState();
    const onModelUpdated = () => updateState();

    api.addEventListener('selectionChanged', onSelectionChanged);
    api.addEventListener('modelUpdated', onModelUpdated);

    updateState();

    return () => {
      api.removeEventListener('selectionChanged', onSelectionChanged);
      api.removeEventListener('modelUpdated', onModelUpdated);
    };
  }, [api, updateState]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const handleToggle = useCallback(() => {
    if (!api) return;

    const shouldSelect = !checked;

    api.forEachNode(node => {
      if (node.data) {
        node.setSelected(shouldSelect);
      }
    });
  }, [api, checked]);

  const wrapperClass = classNames('ag-wrapper', 'ag-input-wrapper', 'ag-checkbox-input-wrapper', {
    'ag-checked': checked && !indeterminate,
    'ag-indeterminate': indeterminate,
  });

  return (
    <div ref={wrapperRef} className="ag-selection-checkbox">
      <div className={wrapperClass} role="presentation">
        <input
          ref={inputRef}
          className="ag-input-field-input ag-checkbox-input"
          type="checkbox"
          checked={checked}
          onChange={handleToggle}
          aria-label="Select all rows"
        />
      </div>
    </div>
  );
}
